import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  AnalysisSessionDetail,
  AnalysisSessionList,
  BudgetResponse,
  TriggerType,
} from "@/api/types";

interface StartParams {
  incident_id?: string | null;
  trigger_type: TriggerType;
  target_host?: string | null;
}

interface SendMessageBody {
  type: "message" | "command";
  content?: string;
  command?: string;
  args?: string[];
}

const KEYS = {
  session: (id: string) => ["analysis", "session", id] as const,
  list: (filters: Record<string, unknown>) => ["analysis", "list", filters] as const,
  budget: ["analysis", "budget"] as const,
  chats: ["analysis", "chats"] as const,
};

export function useStartAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: StartParams) =>
      api<AnalysisSessionDetail>("/api/v1/analysis/start", {
        method: "POST",
        body: params,
      }),
    onSuccess: (data) => {
      qc.setQueryData(KEYS.session(data.id), data);
    },
  });
}

export function useAnalysisSession(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.session(id) : ["analysis", "session", "noop"],
    queryFn: () =>
      api<AnalysisSessionDetail>(`/api/v1/analysis/sessions/${id}`),
    enabled: Boolean(id),
    refetchInterval: 5000,
  });
}

export function useSendMessage(sessionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendMessageBody) =>
      api<AnalysisSessionDetail>(
        `/api/v1/analysis/sessions/${sessionId}/messages`,
        { method: "POST", body },
      ),
    onSuccess: (data) => {
      if (sessionId) qc.setQueryData(KEYS.session(sessionId), data);
    },
  });
}

export function useCloseSession(sessionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<AnalysisSessionDetail>(
        `/api/v1/analysis/sessions/${sessionId}/close`,
        { method: "POST" },
      ),
    onSuccess: (data) => {
      if (sessionId) qc.setQueryData(KEYS.session(sessionId), data);
    },
  });
}

export function useAnalysisSessions(filters: {
  status?: string;
  incident_id?: string;
  page?: number;
  per_page?: number;
}) {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.incident_id) params.set("incident_id", filters.incident_id);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.per_page) params.set("per_page", String(filters.per_page));
  const qs = params.toString();
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () =>
      api<AnalysisSessionList>(
        qs
          ? `/api/v1/analysis/sessions?${qs}`
          : "/api/v1/analysis/sessions",
      ),
  });
}

export function useBudget() {
  return useQuery({
    queryKey: KEYS.budget,
    queryFn: () => api<BudgetResponse>("/api/v1/analysis/budget"),
    refetchInterval: 30_000,
  });
}

// Hablando con AROC: las pláticas propias de la web.
export function useChatSessions() {
  return useQuery({
    queryKey: KEYS.chats,
    queryFn: () =>
      api<AnalysisSessionList>(
        "/api/v1/analysis/sessions?mine=true&trigger_type=chat&per_page=30",
      ),
    refetchInterval: 30_000,
  });
}

export function useOpenChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<AnalysisSessionDetail>("/api/v1/analysis/chat", { method: "POST" }),
    onSuccess: (data) => {
      qc.setQueryData(KEYS.session(data.id), data);
      qc.invalidateQueries({ queryKey: KEYS.chats });
    },
  });
}
